/**
 * CourseStagePanel — generic wrapper for course pipeline stage UI.
 *
 * Mirrors the article pipeline's StagePanel exactly, but reads from
 * CourseContext and uses courseRunsService for artifacts.
 */

import React, { useCallback, useState } from 'react';
import { useCourseContext } from '../CourseContext';
import { LessonRunProgress } from './LessonRunProgress';
import { useArtifact } from '../../ContentManagementProject/shared/useArtifact';
import { CourseStagePanelDetails } from './CourseStagePanelDetails';
import type { CourseDocument } from '../../../services/courseRunsService';
import type { JobRecord } from '../../../services/jobsService';
import { courseRunsService } from '../../../services/courseRunsService';

export interface CourseStagePanelProps {
  stageId: string;
  title: string;
  description?: string;
  children?: React.ReactNode;
  renderOutput?: (document: CourseDocument) => React.ReactNode;
  runDisabled?: boolean;
  runDisabledReason?: string;
  runLabel?: string;
  hideRunButton?: boolean;
  isLlmStage?: boolean;
  showPromptEditor?: boolean;
  extractionId?: string;
  buildPayload?: () => Record<string, unknown>;
}

export function CourseStagePanel({
  stageId,
  title,
  description,
  children,
  renderOutput,
  runDisabled,
  runDisabledReason,
  runLabel,
  hideRunButton,
  isLlmStage,
  showPromptEditor,
  extractionId,
  buildPayload,
}: CourseStagePanelProps) {
  const { document, run, executeStage, refreshDocument, loading: docLoading, isExecuting } = useCourseContext();
  const runId = run?.run_id;

  const [showRerunConfirm, setShowRerunConfirm] = useState(false);
  const [running, setRunning] = useState(false);
  const [jobStatus, setJobStatus] = useState<string | null>(null);
  const [jobProgress, setJobProgress] = useState<any[]>([]);
  const [jobError, setJobError] = useState<any>(null);

  // Lazy-loaded artifacts from S3
  const prompt = useArtifact(runId, stageId, courseRunsService.getStagePrompt, !!isLlmStage, extractionId);
  const rawOutput = useArtifact(runId, stageId, courseRunsService.getStageRaw, !!isLlmStage, extractionId);

  const isStageCompleted = (run?.stages || []).some(
    (s) => s.stage_id === stageId && s.status === 'succeeded'
  );

  const handleRunConfirmed = useCallback(async () => {
    setRunning(true);
    setJobStatus('queued');
    setJobProgress([]);
    setJobError(null);
    try {
      const payload = buildPayload ? buildPayload() : undefined;
      await executeStage(stageId, {
        payload,
        onUpdate: (job: JobRecord) => {
          setJobStatus(job.status || null);
          if (Array.isArray(job.progress)) {
            setJobProgress(job.progress);
          }
        },
      });
      setJobStatus('succeeded');
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Stage execution failed';
      setJobError(msg);
      setJobStatus('failed');
    } finally {
      setRunning(false);
    }
  }, [stageId, executeStage, buildPayload]);

  const handleRunClick = useCallback(() => {
    if (isStageCompleted) {
      setShowRerunConfirm(true);
    } else {
      handleRunConfirmed();
    }
  }, [isStageCompleted, handleRunConfirmed]);

  const handleRerun = useCallback(async () => {
    setShowRerunConfirm(false);
    await handleRunConfirmed();
  }, [handleRunConfirmed]);

  const externalJobRunning = isExecuting && !running;
  const isDisabled = runDisabled || running || docLoading || externalJobRunning;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div>
        <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
        {description && (
          <p className="text-xs text-gray-500 mt-0.5">{description}</p>
        )}
      </div>

      {/* External job running banner */}
      {externalJobRunning && (
        <div className="bg-amber-50 border border-amber-200 rounded px-3 py-2 text-sm text-amber-800">
          A job is currently running for this course. Please wait for it to complete.
        </div>
      )}

      {/* Custom content */}
      {children}

      {/* Action buttons */}
      <div className="flex items-center gap-3">
        {!hideRunButton && (
          <button
            type="button"
            className="px-3 py-1.5 text-sm rounded-md text-white hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ backgroundColor: '#1a2332' }}
            disabled={isDisabled}
            title={isDisabled ? (externalJobRunning ? 'Another job is running' : runDisabledReason || (running ? 'Running...' : '')) : undefined}
            onClick={handleRunClick}
          >
            {running ? 'Running...' : externalJobRunning ? 'Job in progress...' : (runLabel || `Run ${title}`)}
          </button>
        )}
        {!hideRunButton && jobStatus && jobStatus !== 'succeeded' && jobStatus !== 'failed' && (
          <span className="text-xs text-gray-500">{jobStatus}</span>
        )}
      </div>

      {/* Progress */}
      {(running || jobProgress.length > 0) && (
        <LessonRunProgress jobStatus={jobStatus} jobProgress={jobProgress} />
      )}

      {/* Error */}
      {jobError && (
        <div className="bg-red-50 border border-red-200 rounded p-3 text-sm text-red-700">
          {typeof jobError === 'string' ? jobError : JSON.stringify(jobError)}
        </div>
      )}

      {/* Output */}
      {document && renderOutput && (
        <div className="border-t border-gray-200 pt-4">
          {renderOutput(document)}
        </div>
      )}

      {/* Details (collapsed by default) */}
      <CourseStagePanelDetails
        stageId={stageId}
        title={title}
        runId={runId}
        isLlmStage={isLlmStage}
        showPromptEditor={showPromptEditor}
        isStageCompleted={isStageCompleted}
        prompt={prompt}
        rawOutput={rawOutput}
        jobStatus={jobStatus}
        jobError={jobError}
        jobProgress={jobProgress}
      />

      {/* Rerun confirmation */}
      {showRerunConfirm && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/40" onClick={() => setShowRerunConfirm(false)}>
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-5 space-y-4 border border-gray-200" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-base font-semibold text-gray-900">Rerun this stage?</h3>
            <p className="text-sm text-gray-600">
              This will regenerate <strong>{title}</strong>. The previous output will be overwritten.
            </p>
            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                className="px-3 py-1.5 text-sm border rounded-md border-gray-300 text-gray-700 hover:bg-gray-50"
                onClick={() => setShowRerunConfirm(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="px-3 py-1.5 text-sm rounded-md text-white hover:opacity-90"
                style={{ backgroundColor: '#1a2332' }}
                onClick={handleRerun}
              >
                Rerun
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
